import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { logger } from '../../datadog';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { videoSDKAdapterAtom } from '../core/atoms';
import { recordingStateAtom } from '../core/message/atoms';
import { AudioRecordingState, controlAudioRecordingMutex } from '../core/message/constants';

type UseStartAudioRecordingProps = {
  canStart: () => boolean;
  handleStart: () => Promise<void>;
  handleAfterStart: () => void;
};

export function useStartAudioRecording(props: UseStartAudioRecordingProps) {
  const { canStart, handleStart, handleAfterStart } = props;

  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setRecordingState = useSetAtom(recordingStateAtom);

  return useCallback(
    ({ lastUpdated = Date.now() }: { lastUpdated?: number } = {}) => {
      return controlAudioRecordingMutex.runExclusive(async () => {
        const recordingCanBeStarted = canStart();
        if (!recordingCanBeStarted) return;

        sendLoggingEvents(
          TrackingEvents.STARTING_AUDIO_RECORDING,
          {},
          {
            logLevel: 'info',
            message: 'Starting audio recording',
          },
        );

        const { success, error } = await getAdapter().startAudioRecording(handleStart);

        if (!success) {
          logger.error('Failed to start recording audio', {}, error);
          throw error;
        }

        handleAfterStart();
        setRecordingState({
          value: AudioRecordingState.RECORDING,
          lastUpdated,
        });
      }, 'useStartAudioRecording');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

type UseStopAudioRecordingProps = {
  handleStop: (shouldTeardown: boolean) => Promise<void>;
  handleAfterStop: (shouldBroadcastState: boolean) => void;
};

interface StopRecordingOptions {
  lastUpdated?: number;
  newRecordingState?: AudioRecordingState;
  shouldBroadcastState?: boolean;
  shouldTeardown?: boolean;
}

interface SafelyStopRecordingOptions extends StopRecordingOptions {
  /**
   * When set to true, the stop recording operation will run without the
   * {@link controlAudioRecordingMutex} mutex lock. This is useful when already
   * running inside of the mutex to avoid deadlocks.
   */
  skipMutexLock?: boolean;
}

export function useStopAudioRecording(props: UseStopAudioRecordingProps) {
  const { handleStop, handleAfterStop } = props;

  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const getIsRecording = useAtomCallbackMemoized(
    (get) => get(recordingStateAtom).value === AudioRecordingState.RECORDING,
    [],
  );
  const setRecordingState = useSetAtom(recordingStateAtom);

  const stopRecording = useCallback(
    async ({
      lastUpdated = Date.now(),
      newRecordingState = AudioRecordingState.NOT_RECORDING,
      shouldBroadcastState = false,
      shouldTeardown = false,
    }: StopRecordingOptions) => {
      const wasRecording = getIsRecording();

      sendLoggingEvents(
        TrackingEvents.STOPPING_AUDIO_RECORDING,
        { willUpload: wasRecording },
        {
          logLevel: 'info',
          message: 'Stopping, getting, and uploading audio recording',
        },
      );
      const { success, error } = await getAdapter().stopAudioRecording(async function handle() {
        await handleStop(shouldTeardown);
      });

      if (!success) {
        logger.error('Failed to stop audio recording', {}, error);
        throw error;
      }

      setRecordingState({ value: newRecordingState, lastUpdated });
      handleAfterStop(shouldBroadcastState);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return useCallback(
    ({ skipMutexLock, ...options }: SafelyStopRecordingOptions = {}) => {
      const stopRecordingWithOptions = () => stopRecording(options);
      return skipMutexLock
        ? stopRecordingWithOptions()
        : controlAudioRecordingMutex.runExclusive(stopRecordingWithOptions, 'useStopAudioRecording');
    },
    [stopRecording],
  );
}
